import React, { useState, useEffect } from 'react';
import { getRoles, getRole, createRole, updateRole, deleteRole, getPermissions } from './adminApi';
import './Roles.css';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentRole, setCurrentRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true,
    permission_ids: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const result = await getRoles();
      setRoles(result);
    } catch (err) {
      console.error('获取角色列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const result = await getPermissions();
      setPermissions(result);
    } catch (err) {
      console.error('获取权限列表失败:', err);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ name: '', code: '', description: '', is_active: true, permission_ids: [] });
    setCurrentRole(null);
    setShowModal(true);
  };

  const openEditModal = async (role) => {
    try {
      const detail = await getRole(role.id);
      setModalMode('edit');
      setCurrentRole(role);
      setFormData({
        name: detail.name,
        code: detail.code,
        description: detail.description || '',
        is_active: detail.is_active,
        permission_ids: detail.permissions?.map(p => p.id) || []
      });
      setShowModal(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentRole(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await createRole(formData);
      } else {
        await updateRole(currentRole.id, formData);
      }
      closeModal();
      fetchRoles();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      alert('系统内置角色不能删除');
      return;
    }
    if (!window.confirm(`确定要删除角色 "${role.name}" 吗？`)) return;
    try {
      await deleteRole(role.id);
      fetchRoles();
    } catch (err) {
      alert(err.message);
    }
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permId)
        ? prev.permission_ids.filter(id => id !== permId)
        : [...prev.permission_ids, permId]
    }));
  };

  const toggleModulePermissions = (modulePerms, checked) => {
    const modulePermIds = modulePerms.map(p => p.id);
    setFormData(prev => ({
      ...prev,
      permission_ids: checked
        ? [...new Set([...prev.permission_ids, ...modulePermIds])]
        : prev.permission_ids.filter(id => !modulePermIds.includes(id))
    }));
  };

  return (
    <div className="roles-page">
      <div className="page-header">
        <h2 className="page-title">角色权限管理</h2>
        <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
          + 新增角色
        </button>
      </div>

      {/* 角色列表 */}
      <div className="admin-card">
        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>角色名称</th>
                <th>角色代码</th>
                <th>描述</th>
                <th>状态</th>
                <th>类型</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <td>{role.id}</td>
                  <td>{role.name}</td>
                  <td><code>{role.code}</code></td>
                  <td>{role.description || '-'}</td>
                  <td>
                    <span className={`admin-tag ${role.is_active ? 'admin-tag-success' : 'admin-tag-error'}`}>
                      {role.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td>
                    {role.is_system ? (
                      <span className="admin-tag admin-tag-info">系统</span>
                    ) : (
                      <span className="admin-tag admin-tag-warning">自定义</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="admin-btn admin-btn-sm admin-btn-default" onClick={() => openEditModal(role)}>
                        编辑权限
                      </button>
                      {!role.is_system && (
                        <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(role)}>
                          删除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan="7" className="admin-empty">暂无角色数据</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 模态框 */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal role-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {modalMode === 'create' ? '新增角色' : '编辑角色权限'}
              </h3>
              <button className="admin-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label">角色名称</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">角色代码</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    disabled={modalMode === 'edit'}
                    placeholder="如: editor, viewer"
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">描述</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="角色描述（可选）"
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    启用角色
                  </label>
                </div>
                
                {/* 权限矩阵 */}
                <div className="admin-form-group">
                  <label className="admin-form-label">权限配置</label>
                  <div className="permission-matrix">
                    {Object.entries(permissions).map(([module, perms]) => {
                      const allSelected = perms.every(p => formData.permission_ids.includes(p.id));
                      const someSelected = perms.some(p => formData.permission_ids.includes(p.id));
                      return (
                        <div key={module} className="permission-module">
                          <div className="module-header">
                            <label className="module-checkbox">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => el && (el.indeterminate = someSelected && !allSelected)}
                                onChange={(e) => toggleModulePermissions(perms, e.target.checked)}
                              />
                              <strong>{module === 'admin' ? '管理后台' : module}</strong>
                            </label>
                          </div>
                          <div className="module-permissions">
                            {perms.map(perm => (
                              <label key={perm.id} className="permission-item">
                                <input
                                  type="checkbox"
                                  checked={formData.permission_ids.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                />
                                {perm.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-default" onClick={closeModal}>
                  取消
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  {modalMode === 'create' ? '创建' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;